<template>
  <div class="auth-screen">
    <div class="auth-card">
      <!-- ===== Left: form ===== -->
      <div class="auth-form">
        <div class="auth-brand">
          <span class="auth-logo">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 2.5 20.5 7v10L12 21.5 3.5 17V7L12 2.5Z"
                fill="#111111"
              />
              <path d="M12 2.5 20.5 7 12 11.5 3.5 7 12 2.5Z" fill="#2b2b2b" />
              <path d="M12 11.5 20.5 7v10L12 21.5v-10Z" fill="#000000" />
            </svg>
          </span>
          <span class="auth-brand__name">Transcriber</span>
        </div>

        <div class="auth-form__body">
          <h1 class="auth-title">С возвращением</h1>
          <p class="auth-subtitle">Войдите, чтобы продолжить</p>

          <div class="auth-fields">
            <label class="auth-field">
              <span class="auth-field__label">Логин</span>
              <el-input
                v-model="username"
                placeholder="Введите логин"
                size="large"
              ></el-input>
            </label>

            <label class="auth-field">
              <span class="auth-field__label">Пароль</span>
              <el-input
                show-password
                type="password"
                v-model="password"
                placeholder="Введите пароль"
                size="large"
                @keydown.enter.prevent="login"
              ></el-input>
            </label>

            <button
              :disabled="loading"
              @click="login"
              type="submit"
              class="auth-submit"
            >
              <i v-if="loading" class="el-icon-loading"></i>
              <span>Войти</span>
              <svg
                v-if="!loading"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        </div>

        <div class="auth-foot">
          <span>© 2025 Transcriber</span>
          <span class="auth-foot__links">
            <a href="#">Условия</a>
            <a href="#">Конфиденциальность</a>
          </span>
        </div>
      </div>

      <!-- ===== Right: promo ===== -->
      <div class="auth-promo">
        <div class="auth-promo__top">
          <h2 class="auth-promo__title">
            Платформа для транскрибации и&nbsp;разметки аудио
          </h2>
          <p class="auth-promo__text">
            Загружайте, размечайте и&nbsp;верифицируйте аудиофайлы в&nbsp;едином
            рабочем пространстве — быстро, точно и&nbsp;совместно.
          </p>

          <blockquote class="auth-quote">
            <p class="auth-quote__text">
              «Transcriber заметно ускорил нашу работу: разметка и&nbsp;проверка
              стали в&nbsp;разы удобнее, а&nbsp;команда — синхроннее.»
            </p>
            <div class="auth-quote__author">
              <span class="auth-quote__avatar">АК</span>
              <span class="auth-quote__meta">
                <span class="auth-quote__name">Айгерим К.</span>
                <span class="auth-quote__role">Верификатор</span>
              </span>
            </div>
          </blockquote>
        </div>

        <div class="auth-promo__bottom">
          <div class="auth-trust">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
            >
              <path
                d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3Z"
                stroke-linejoin="round"
              />
              <path
                d="m9 12 2 2 4-4"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>Нам доверяют ведущие команды</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { login, whoami } from "@/api/auth";
import { notify } from "@/services/notify";

export default {
  name: "index",
  data: () => ({
    username: null,
    password: null,
    loading: false,
    forgotForm: "",
  }),
  methods: {
    async login() {
      this.loading = true;
      try {
        await login(this.username, this.password);
        notify({
          message: "Успешно",
          type: "success",
          customClass: "my-custom-message",
          duration: 3000,
        });
        const user = await whoami();
        this.$store.commit("user/setUser", user);
        this.$router.push({ name: "Home" });
      } catch (e) {
        this.$message.error(e.message);
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.auth-screen {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background: var(--canvas);

  // Light: barely-there cool tint, mostly white (matches reference)
  --auth-gradient: linear-gradient(
    140deg,
    rgba(59, 130, 246, 0.025),
    rgba(139, 92, 246, 0.035) 60%,
    rgba(16, 185, 129, 0.02)
  );
}

// Dark: a touch more presence so the panel doesn't read as flat black
:global(html.dark) .auth-screen {
  --auth-gradient: linear-gradient(
    140deg,
    rgba(59, 130, 246, 0.06),
    rgba(139, 92, 246, 0.08) 60%,
    rgba(16, 185, 129, 0.05)
  );
}

/* Split card — outer container carries the promo gradient full-width */
.auth-card {
  display: flex;
  align-items: stretch;
  width: 100%;
  max-width: 1180px;
  height: min(760px, calc(100vh - 56px));
  padding: 6px;
  gap: 6px;
  background: var(--auth-gradient);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  animation: auth-card-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes auth-card-in {
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

// Inner content settles in just after the card, staggered for a softer feel
.auth-form__body,
.auth-promo {
  animation: auth-fade-up 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
}
.auth-promo {
  animation-delay: 0.2s;
}

@keyframes auth-fade-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-card,
  .auth-form__body,
  .auth-promo {
    animation: none;
  }
}

/* ===== Left: form — its own raised white island ===== */
.auth-form {
  flex: 1 1 60%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 32px 40px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 5px;
  box-shadow: var(--shadow-sm);
}

.auth-brand {
  display: flex;
  align-items: center;
  gap: 9px;
}
.auth-logo {
  width: 28px;
  height: 28px;
  display: inline-flex;
  svg {
    width: 28px;
    height: 28px;
  }
}
.auth-brand__name {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.4px;
  color: var(--foreground);
}

.auth-form__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 360px;
  width: 100%;
  margin: 0 auto;
}

.auth-title {
  margin: 0;
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.6px;
  color: var(--foreground);
}
.auth-subtitle {
  margin: 6px 0 28px;
  text-align: center;
  font-size: 14px;
  color: var(--muted-foreground);
}

.auth-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.auth-field {
  display: block;

  &__label {
    display: block;
    margin-bottom: 7px;
    font-size: 13px;
    font-weight: 600;
    color: var(--foreground);
  }

  // shadcn input look
  :deep(.el-input__wrapper) {
    border-radius: 10px;
    background: var(--card);
    box-shadow: 0 0 0 1px var(--input) inset;
    padding: 2px 14px;
    transition: box-shadow 0.18s ease, background-color 0.18s ease;
    &:hover {
      box-shadow: 0 0 0 1px var(--border-strong) inset;
    }
    &.is-focus {
      box-shadow: 0 0 0 1px var(--border-strong) inset, 0 0 0 3px var(--ring);
    }
  }
  :deep(.el-input__inner) {
    color: var(--foreground);
    font-size: 14px;
    background-color: transparent;
    &::placeholder {
      color: var(--muted-foreground);
    }
  }
  :deep(.el-input__password),
  :deep(.el-input__icon) {
    color: var(--muted-foreground);
  }

  // Kill the browser autofill yellow/blue background; keep our theme colors
  :deep(.el-input__inner:-webkit-autofill),
  :deep(.el-input__inner:-webkit-autofill:hover),
  :deep(.el-input__inner:-webkit-autofill:focus),
  :deep(.el-input__inner:-webkit-autofill:active) {
    -webkit-text-fill-color: var(--foreground);
    -webkit-box-shadow: 0 0 0 1000px var(--card) inset;
    box-shadow: 0 0 0 1000px var(--card) inset;
    caret-color: var(--foreground);
    transition: background-color 9999s ease-in-out 0s;
  }
}

.auth-submit {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 46px;
  border: 0;
  border-radius: 11px;
  background: var(--primary);
  color: var(--primary-foreground);
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.05s ease;

  svg {
    width: 17px;
    height: 17px;
  }
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.auth-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12.5px;
  color: var(--muted-foreground);

  &__links {
    display: flex;
    gap: 18px;
    a {
      color: var(--muted-foreground);
      text-decoration: none;
      &:hover {
        color: var(--foreground);
      }
    }
  }
}

/* ===== Right: promo ===== */
.auth-promo {
  position: relative;
  flex: 1 1 40%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 44px 40px;
  overflow: hidden;
  // Layer 1 — soft diagonal base wash
  background: linear-gradient(
    155deg,
    rgba(59, 130, 246, 0.07),
    rgba(139, 92, 246, 0.09) 55%,
    rgba(16, 185, 129, 0.05)
  );

  // Layer 2 — a centered vertical glow strip ("sprayed" down the middle)
  &::before {
    content: "";
    position: absolute;
    top: -10%;
    bottom: -10%;
    left: 50%;
    width: 62%;
    transform: translateX(-50%);
    background: radial-gradient(
      55% 60% at 50% 42%,
      rgba(124, 110, 245, 0.1),
      rgba(124, 110, 245, 0.04) 45%,
      transparent 72%
    );
    filter: blur(30px);
    pointer-events: none;
    z-index: 0;
  }
  // keep content above the glow
  > * {
    position: relative;
    z-index: 1;
  }
}

.auth-promo__title {
  margin: 0;
  font-size: 26px;
  line-height: 1.18;
  font-weight: 800;
  letter-spacing: -0.6px;
  color: var(--foreground);
}
.auth-promo__text {
  margin: 14px 0 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--muted-foreground);
  max-width: 440px;
}

.auth-quote {
  margin: 32px 0 0;
  padding-left: 16px;
  border-left: 3px solid #6366f1;

  &__text {
    margin: 0;
    font-size: 13.5px;
    line-height: 1.6;
    color: var(--foreground);
  }
  &__author {
    margin-top: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  &__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #3b82f6, #8b5cf6);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
  }
  &__meta {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
  }
  &__name {
    font-size: 14px;
    font-weight: 600;
    color: var(--foreground);
  }
  &__role {
    font-size: 12.5px;
    color: var(--muted-foreground);
  }
}

.auth-trust {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--muted-foreground);

  svg {
    width: 18px;
    height: 18px;
    color: #22c55e;
  }
}

/* Dark: invert the black cube + soften promo gradient */
:global(html.dark) .auth-logo svg path {
  &[fill="#111111"],
  &[fill="#000000"] {
    fill: #fafafa;
  }
  &[fill="#2b2b2b"] {
    fill: #d4d4d8;
  }
}

/* Stack on small screens — hide promo */
@media (max-width: 860px) {
  .auth-promo {
    display: none;
  }
  .auth-form {
    flex-basis: 100%;
  }
}
</style>
